import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    forwardRef,
    input,
    InputSignal,
    Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { startWith } from 'rxjs';

export interface ListItem {
  id: string | number;
  [key: string]: unknown;
}

@Component({
  selector: 'siat-checkbox-list-field',
  templateUrl: './checkbox-list.component.html',
  styleUrls: ['./checkbox-list.component.scss'],
   
  imports: [
    CommonModule,
    MatIcon,
    MatListModule,
    MatCheckboxModule,
    FormsModule,
    MatTooltip,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxListFieldComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxListFieldComponent<T extends ListItem>
  implements ControlValueAccessor
{
  /**
   * The form group associated with the input field.
   */
  public formGroup: InputSignal<FormGroup> = input.required<FormGroup>();

  /**
   * The control name associated with the input field.
   */
  public controlName: InputSignal<string> = input.required<string>();

  /**
   * The list of items to display. The component is generic over type T,
   * which must extend the ListItem interface (i.e., have an 'id' property).
   */
  public items: InputSignal<T[]> = input.required<T[]>();

  /**
   * The property key of the item object to use for the label display.
   * This makes the component highly reusable.
   * @example <siat-checkbox-list-field itemLabelKey="name" ... />
   */
  public itemLabelKey: InputSignal<keyof T> = input.required<keyof T>();

  /**
   * The property key of the item object to use for the mat-tooltip content.
   */
  public itemTooltipKey: InputSignal<keyof T> = input.required<keyof T>();

  /**
   * The maximum height of the list container before scrolling is enabled.
   */
  public maxHeight = input<string>('300px');

  /**
   * The label for the search input field.
   */
  public searchPlaceholder = input<string>('Cerca...');

  /**
   * Form control for the search input field.
   */
  public searchControl = new FormControl('');

  /**
   * A signal that holds the current value of the search input field.
   */
  public searchTermSignal = toSignal(
    this.searchControl.valueChanges.pipe(startWith(''))
  );

  /**
   * Gets the form control associated with the input field from the parent form group.
   */
  public get control(): FormControl<number[] | string[]> {
    return this.formGroup().controls[this.controlName()] as FormControl<
      number[] | string[]
    >;
  }
  /**
   * A computed signal that filters the items based on the search input.
   * This is reactive and will update automatically when the search term changes.
   */
  public filteredItems: Signal<T[]> = computed(() => {
    const allItems = this.items();
    const searchTerm = this.searchTermSignal()?.toLowerCase() ?? '';
    const labelKey = this.itemLabelKey();

    if (!searchTerm) {
      return allItems;
    }

    return allItems.filter(item =>
      String(item[labelKey]).toLowerCase().includes(searchTerm)
    );
  });

  /**
   * Un computed signal che calcola quali elementi sono visibili.
   * Lo usiamo sia per nascondere gli elementi, sia per il messaggio "nessun risultato".
   */
  public visibleItems: Signal<T[]> = computed(() => {
    const allItems = this.items();
    const searchTerm = this.searchTermSignal()?.toLowerCase() ?? '';
    const labelKey = this.itemLabelKey();

    if (!searchTerm) {
      return allItems;
    }

    return allItems.filter(item =>
      String(item[labelKey]).toLowerCase().includes(searchTerm)
    );
  });

  /**
   * Keeps a reference to the function to be called every time
   * the value of the field is changed in the UI.
   */
  onChange: (value: number[] | string[]) => void = () => {};

  /**
   * Keeps a reference to the function to be called every time
   * the field has to be considered touched (normally on blur).
   */
  onTouched: () => void = () => {};

  /**
   * Writes a new value to the element.
   * This method is called by the Forms API to update the view when the model changes.
   * @param value The new value for the element.
   */
  writeValue(value: number[] | string[] | null): void {
    this.control.setValue(value);
  }

  /**
   * Registers a callback function that should be called when the control's value changes in the UI.
   */
  registerOnChange(fn: (value: number[] | string[]) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function that should be called when the control is touched.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
